import { IN_FLIGHT_IMPORT_STATUSES, isInFlightStatus } from '../../../lib/constants';

// Getting a practice's data in, end to end. Mapping is deliberately not one of
// these: it only exists for a CSV, and it belongs to the file you just chose —
// so it is a sub-step of Data (MAP_STEP below), shown as a crumb only while it
// is being done rather than sitting in the bar empty half the time.
//
//   1 Request  → create it (practice, vendor, due date)
//   2 Data     → upload to S3, or locate what's already there  → 2a map columns
//   3 Import   → start it, watch it run
//   4 Review   → what landed, what needs attention
export const PROCESS_STEPS = {
  request: { name: 'request', title: 'Request' },
  data: { name: 'data', title: 'Data' },
  import: { name: 'import', title: 'Import' },
  review: { name: 'review', title: 'Review' }
};

export const PROCESS_STEP_LIST = [
  PROCESS_STEPS.request,
  PROCESS_STEPS.data,
  PROCESS_STEPS.import,
  PROCESS_STEPS.review
];

// Within the Data step: pick a source, then map it if it needs mapping.
export const DATA_SUB_STEPS = { choose: 'choose', map: 'map' };

// Which files the CSV wizard can map. Anything else (a vendor archive, a CCDA
// folder) goes to that vendor's own importer untouched.
export const isMappableCsv = (fileName = '') => /\.(csv|tsv)$/i.test(String(fileName));

// An import_info / patient_imports run still going. While any of these is the
// current run, the Import step shows progress instead of a start button. Derived
// from the request-level list so there is one definition of "still running".
export const RUNNING_STATUSES = IN_FLIGHT_IMPORT_STATUSES;

export const isRunning = isInFlightStatus;

// A run that will not change again. `finished`/`error` come from import_info,
// `done`/`failed` from patient_imports — a CSV run — so both vocabularies are
// listed rather than assumed equal.
export const TERMINAL_STATUSES = ['finished', 'error', 'done', 'failed'];

export const isTerminal = (status) =>
  TERMINAL_STATUSES.includes(String(status || '').toLowerCase());

// Which of those two vocabularies mean it worked — `finished` from import_info,
// `done` from patient_imports. Everything else terminal is a failure, derived
// rather than listed again so the two lists cannot drift apart.
export const SUCCESS_STATUSES = ['finished', 'done'];

export const FAILURE_STATUSES = TERMINAL_STATUSES.filter(
  (status) => !SUCCESS_STATUSES.includes(status)
);

export const isSuccessStatus = (status) =>
  SUCCESS_STATUSES.includes(String(status || '').toLowerCase());

/**
 * How many of a run's records an operator can still act on.
 *
 * Only a CSV run has per-record resolutions (`patient_import_resolution`), and
 * only these four of the worker's five summary buckets are actionable — `success`
 * landed cleanly and has nothing to decide. An EHR run reports no buckets, so it
 * is always 0 and is sent to its logs instead.
 */
export const REVIEWABLE_BUCKETS = ['info', 'partial', 'duplicates', 'errors'];

export const reviewableCount = (run) => {
  if (run?.kind !== 'csv') return 0;
  const summary = run.summary || {};
  return REVIEWABLE_BUCKETS.reduce((total, bucket) => total + (summary[bucket] || 0), 0);
};

// The crumb that only exists while a chosen CSV is being mapped — a sub-step of
// Data, pushed into the bar so the operator can see where they are.
export const MAP_STEP = { name: 'map', title: 'Map columns' };

export const STEP_ICONS = {
  request: 'clipboard-text-mono',
  data: 'folder-mono',
  map: 'list-unordered-mono',
  import: 'play-mono',
  review: 'eye-mono'
};

export const DONE_ICON = 'checkbox-circle-mono';
